// FROZEN test-hook surface. This is the `window.__olm` object that the headless
// tests (test/smoke.mjs, test/auth.mjs, test/batch.mjs, …) assert against.
// It was moved here VERBATIM from main.ts so the toolbar refactor cannot
// accidentally change the shape tests depend on. Phase B agents MUST NOT remove
// or rename any property here; only additive changes are allowed.

import { state, getCanvas } from './state'
import { refreshGrid, applyCanvasShape } from './canvas'
import { normalizeLabelSize, findLabelSize, normalizeSizeAndSheet, findSheetLayout, resolveCatalogueId, sheetCapacity, LABEL_SIZES, SHEET_LAYOUTS } from './constants'
import { startCheckout } from './billing'
import { fitTextToWidth, fitAllTexts } from './widgets/autofit'
import { readDeepLink, applySizeDeepLink } from './deeplink'
import { addText } from './widgets/text'
import { addShape } from './widgets/shapes'
import { addBarcode } from './widgets/barcode'
import { addQRCode } from './widgets/qrcode'
import {
  addTable, addRow as tableAddRow, setCell as tableSetCell, getTableModel, getTableStyle,
  setCellFill, setCellStroke, setBorderWidth, isTable,
  getTableTextStyle, setCellTextAlign, setCellVAlign, setCellFontSize, setCellTextColor,
  setCellBorderStyle, toggleCellBold, toggleCellItalic,
} from './widgets/table-widget'
import { undo, redo } from './history'
import { batchRenderAllLabels, detectBatchFields } from './batch/engine'
import { parseSpreadsheetBuffer, parseDelimitedText } from './batch/import'
import { batchPdfBytes } from './batch/export'
import { exportPNG, exportPDF, generateZPL, printCurrentLabel, printPageHtml } from './export'
import { isThermalDieCut } from './print-thermal'
import { clipImageToShape } from './shape-clip'
import { applySizeById } from './panels/sizePicker'
import { supabase } from './auth/supabase'
import { openAuth } from './auth/auth-ui'
import { effectiveTier } from './auth/org'
import { requireTier } from './billing'
import { listDesigns, loadCloudDesign } from './cloud/designs'
import { migrateDesignJSON, needsMigration } from './migration'
import { lq, lqError } from './liquid'
import { addDataTable, isDataTable, addSummaryTable, setStaticMode } from './widgets/datatable'
import { addDynamicTable, isDynamicTable, setSelectedCellText, DynamicTable } from './widgets/dynamic-table'
import { buildFormula } from './panels/formula-builder'
import { addPage, switchPage, deletePage, getPages, pageCount, getCurrentPageIndex } from './pages'
import { saveOlmObject, loadOlmJson } from './olmfile'
import { ensureAdGate, adFreeUntil, setAdFreeUntil, gateApplies } from './ads/gate'
import { overrideAdsConfig, adsConfig } from './ads/config'
import { refreshAdPlacements } from './ads/placements'
import { PRODUCT } from './product'
import { openAdGate } from './panels/adGateModal'
import { _setTestProvider } from './ads/providers/types'
import { _resetAdblockCache } from './ads/adblock'

/**
 * Install the frozen `window.__olm` test/debug surface. Called once from boot().
 * Mirrors the legacy editor's window.* exports.
 */
export function installDevHooks(): void {
  ;(window as any).__olm = {
    state,
    getCanvas,
    addText,
    addShape,
    addBarcode,
    addQRCode,
    addTable,
    addDataTable,
    isDataTable,
    addSummaryTable,
    setStaticMode,
    tableAddRow,
    tableSetCell,
    getTableModel,
    getTableStyle,
    setCellFill,
    setCellStroke,
    setBorderWidth,
    getTableTextStyle,
    setCellTextAlign,
    setCellVAlign,
    setCellFontSize,
    setCellTextColor,
    setCellBorderStyle,
    toggleCellBold,
    toggleCellItalic,
    isTable,
    undo,
    redo,
    objectCount: () => getCanvas().getObjects().filter((o) => !(o as any).isGrid).length,
    // Batch test hooks
    setBatchData: (headers: string[], rows: Record<string, string>[]) => {
      state.batchHeaders = headers; state.batchData = rows
      document.dispatchEvent(new CustomEvent('olm:batchchange')) // sync the toolbar Field ▾ menu
    },
    detectBatchFields,
    batchRenderAllLabels,
    parseSpreadsheetBuffer, // real import path (date-format regression coverage)
    parseDelimitedText,
    batchPdfBytes,
    lq, // Liquid eval (shared engine with the server renderer)
    lqError, // strict template validation (export pre-check)
    buildFormula, // formula-builder Liquid generator (pure)
    // Export + size test hooks
    exportPNG,
    exportPDF,
    generateZPL,
    printCurrentLabel, // direct print (export suite stubs window.open)
    printPageHtml,
    applySizeById,
    isThermalDieCut, // DYMO-style die-cut detection (rounded-corner preview)
    clipImageToShape, // round/oval die-cut clip (sheet preview + export)
    // Auth/billing test hooks (read-only / pure; tests must NOT mutate prod data)
    supabase,
    effectiveTier,
    requireTier,
    listDesigns,
    loadCloudDesign,
    openAuth,
    // Migration test hooks
    migrateDesignJSON,
    needsMigration,
    loadJSON: async (json: unknown) => { const c = getCanvas(); await c.loadFromJSON(json as any); c.requestRenderAll() },
    // Multi-page (.olm v2) test hooks
    addPage, switchPage, deletePage, getPages, pageCount, getCurrentPageIndex,
    saveOlmObject, loadOlmJson,
    // Dynamic table (spreadsheet-style data table) test hooks
    addDynamicTable, isDynamicTable, setSelectedCellText, DynamicTable,
    // Ad-gate test hooks (additive; see src/ads/)
    adGate: {
      ensureAdGate,
      openAdGate,
      adFreeUntil,
      setAdFreeUntil,
      gateApplies,
      overrideAdsConfig,
      adsConfig,
      _setTestProvider,
      _resetAdblockCache,
      refreshAdPlacements,
    },
    // Active product profile (read-only; tests branch on profile-gated UI)
    product: PRODUCT,
    // Canvas chrome hooks (grid overlay + label shape; ui-fixes suite)
    refreshGrid,
    applyCanvasShape,
    // Size catalogue helpers (classic cloud designs / legacy ids; migration + catalogue suites)
    normalizeLabelSize,
    findLabelSize,
    normalizeSizeAndSheet,
    findSheetLayout,
    resolveCatalogueId,
    sheetCapacity,
    LABEL_SIZES,
    SHEET_LAYOUTS,
    // Deep links + checkout (deeplink suite; startCheckout is exercised via page.route only)
    readDeepLink,
    applySizeDeepLink,
    startCheckout,
    // Auto-shrink to fit width (batch suite)
    fitTextToWidth,
    fitAllTexts,
    ready: true,
  }
}
